import { IStrongBoxFilter } from '@nkr/models/strong-box/strong-box-filter';
import { Utility } from '@nkr/services/utility';
import { useMemo, useRef, useState } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';
import { LoadingIndicator } from '@nkr/components/loading-indicator';
import { ColDef, GridApi, GridReadyEvent, ICellRendererParams, IHeaderParams, RowClassParams, ValueGetterParams } from 'ag-grid-community';
import { IStrongBoxCenterOrderListing } from '@nkr/models/strong-box/strong-box-order';
import { GridWrapper } from './styles/styles'
import { AgGridReact } from 'ag-grid-react';
import { TLegendEntry } from './legend';
import useStrongBoxOrders from '@nkr/store/strong-box/hooks/use-strong-box-orders';
import StrongBoxFilter from './strong-box-filter';
import SortableHeaderComponent from '../recipients/recipient-shield/components/sortable-header';
import DonorCenterCell from './donor-center-cell';

type IProps = {

}

const StrongBoxOrders = ({ }: IProps) => {
    const defaultFilter: IStrongBoxFilter = {
        startDate: Utility.getFirstOfCurrentMonth(),
        endDate: Utility.getLastOfCurrentMonth(),
        isCenter: true
    }

    const [filter, setFilter] = useState<IStrongBoxFilter>(defaultFilter);
    const { list, loading, error, checkStatus } = useStrongBoxOrders(filter);

    const colDefs: ColDef[] = [
        {
            colId: 'rNum',
            field: 'rNum',
            headerName: 'R#',
            sortable: false,
            width: 20,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>R#</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => <>{params.node.rowIndex + 1}</>,
        },
        {
            colId: 'centerAlias',
            field: 'centerAlias',
            headerName: 'Center',
            width: 120,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Center</SortableHeaderComponent>,
        },
        {
            colId: 'createdDate',
            field: 'createdDate',
            headerName: 'Reported Date',
            width: 120,
            comparator: Utility.dateComparator,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Ordered Date</SortableHeaderComponent>,
        },
        {
            colId: 'internalTrackingNumber',
            field: 'internalTrackingNumber',
            headerName: 'Internal Tracking Number',
            width: 120,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Internal<br />Tracking Number</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                var str = d.internalTrackingNumber;
                if (str && str.length > 14) {
                    str = `${str.slice(0, 4)}...${str.slice(-4)}`;
                }
                return <span title={d.internalTrackingNumber}>{str}</span>
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                return d.internalTrackingNumber;
            },
        },
        {
            colId: 'vendorOrderId',
            field: 'vendorOrderId',
            headerName: 'vendor Order Id',
            width: 120,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Vendor<br />Order ID</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                var str = d.vendorOrderId;
                if (str && str.length > 14) {
                    str = `${str.slice(0, 4)}...${str.slice(-4)}`;
                }
                return <span title={d.vendorOrderId}>{str}</span>
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                return d.vendorOrderId;
            },
        },

        {
            colId: 'quantity',
            field: 'quantity',
            headerName: 'Quantity',
            width: 90,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Quantity</SortableHeaderComponent>,
        },
        {
            colId: 'statusDescription',
            field: 'statusDescription',
            headerName: 'Status',
            width: 120,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Status</SortableHeaderComponent>,
        },
        {
            colId: 'vendorStatus',
            field: 'vendorStatus',
            headerName: 'Vendor Status',
            width: 120,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Vendor Status</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => {
                return !params.data.vendorStatus ? 'N/A' : params.data.vendorStatus;
            },
        },
        {
            colId: 'statusDate',
            field: 'statusDate',
            headerName: 'Status Date',
            width: 120,
            comparator: Utility.dateComparator,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Status Date</SortableHeaderComponent>,
        },
        {
            colId: 'notes',
            field: 'notes',
            headerName: 'Notes',
            width: 150,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Notes</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                if (!d.notes) return <>N/A</>;
                const display = d.notes.length > 25 ? `${d.notes.slice(0, 25)}...` : d.notes;
                return <span title={d.notes}>{display}</span>
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                return d.notes || 'N/A';
            },
        },
        {
            colId: 'compositeSwapNumber',
            field: 'compositeSwapNumber',
            headerName: 'Swap #',
            width: 120,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Swap</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                if (d.swapInfo?.compositeSwapNumber) {
                    return <>{d.swapInfo?.compositeSwapNumber}</>;
                } else {
                    return <>N/A</>;
                }
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                return (d.swapInfo?.compositeSwapNumber) ? d.swapInfo?.compositeSwapNumber : 'N/A';
            },
        },
        {
            colId: 'donorCenter',
            field: 'swapInfo.donorCenter',
            headerName: 'Donor Center',
            width: 155,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Donor Center</SortableHeaderComponent>,
            cellRendererFramework: (params: ICellRendererParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                return <DonorCenterCell donorCenter={d.swapInfo?.donorCenter} remoteDonorCenter={d.swapInfo?.remoteDonorCenter} />;
            },
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                return (d.swapInfo?.donorCenter) ? d.swapInfo?.donorCenter : 'N/A';
            },
        },
        {
            colId: 'recipientCenter',
            field: 'swapInfo.recipientCenter',
            headerName: 'Donor Center',
            width: 140,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Recipient Center</SortableHeaderComponent>,
            valueGetter: (params: ValueGetterParams) => {
                const d: IStrongBoxCenterOrderListing = params.data;
                return (d.swapInfo?.recipientCenter) ? d.swapInfo?.recipientCenter : 'N/A';
            },
        },
        {
            colId: 'kidneyPickupDate',
            field: 'swapInfo.kidneyPickupDate',
            headerName: 'Pickup Date',
            width: 120,
            comparator: Utility.dateComparator,
            headerComponentFramework: (props: IHeaderParams) => <SortableHeaderComponent {...props}>Pickup Date</SortableHeaderComponent>,
        },
        // {
        //     colId: 'action',
        //     field: '',
        //     headerName: '',
        //     sortable: false,
        //     cellRendererFramework: (params: ICellRendererParams) => <><Button bsSize='xs' bsStyle='primary' onClick={() => {checkStatus(params.data.id)}}>Check Status</Button></>,
        //     width: 52
        // },
    ];

    const columnDefs = useMemo(() => colDefs, []);

    const apiRef = useRef<GridApi | null>(null);

    const onGridReady = (params: GridReadyEvent) => {
        apiRef.current = params.api;
        params.api.sizeColumnsToFit();
    };

    const expCsv = () => {
        // Call the API method to trigger download
        if (apiRef.current) {
            apiRef.current.exportDataAsCsv({
                columnGroups: false,
                fileName: `StrongBoxOrderHistory.csv`,
                columnKeys: [
                    'centerName',
                    'createdDate',
                    'internalTrackingNumber',
                    'vendorOrderId',
                    'quantity',
                    'status',
                    'vendorStatus',
                    'statusDate',
                    'notes',
                    'compositeSwapNumber',
                    'donorCenter',
                    'recipeintCenter',
                    'kidneyPickupDate'
                ],
            });
        }
    };

    const getRowStyle = (params: RowClassParams) => {
        const d: IStrongBoxCenterOrderListing = params.data;
        if (d?.swapInfo?.isSwapCancelled) {
            return { background: '#D79E9B' };
        }
    }

    const legendEntries: TLegendEntry[] = [
        { text: 'Cancelled Swap', color: '#D79E9B' }
    ];

    return (<>
        <Row>
            <Col sm={12}>
                <StrongBoxFilter
                    filter={filter}
                    defaultFilter={defaultFilter}
                    startDateLabel='Earliest Order Date'
                    endDateLabel='Latest Order Date'
                    legendEntries={legendEntries}
                    isCenter={true}
                    onChange={setFilter}
                    onExport={expCsv}
                />
            </Col>
        </Row>
        {loading && <LoadingIndicator type='spin' color='black' loading={true} message={'Loading Orders ...'} />}
        {error && <Alert bsStyle='danger'>{JSON.stringify(error)}</Alert>}
        {!loading &&
            <Row>
                <Col sm={12}>
                    <GridWrapper className='ag-theme-bootstrap'>
                        <AgGridReact
                            defaultColDef={{
                                headerClass: 'list-header',
                                cellClass: 'list-cell',
                                sortable: true
                            }}
                            columnDefs={columnDefs}
                            rowData={list}
                            headerHeight={45}
                            rowHeight={32}
                            noRowsOverlayComponentFramework={() => <div>No order history records were found...</div>}
                            suppressRowClickSelection={true}
                            suppressCellSelection={true}
                            enableCellTextSelection={true}
                            onGridReady={onGridReady}
                            onFirstDataRendered={(e) => e.api.sizeColumnsToFit()}
                            onGridSizeChanged={(e) => e.api.sizeColumnsToFit()}
                            onRowDataChanged={(e) => e.api.sizeColumnsToFit()}
                            getRowStyle={getRowStyle}
                        />
                    </GridWrapper>
                </Col>
            </Row>
        }
    </>);
}

export default StrongBoxOrders
